// OnshapeLink — tiny "open this row's model in Onshape" affordance.
//
// Pure display: given an `onshape_ref` (`did/wid/eid`), render a small icon that
// links to the Onshape document in a new tab. Rows without a valid ref render
// nothing (no placeholder). Clicking the icon must NOT select/toggle the row, so
// onClick calls stopPropagation.
//
// Color-blind safe (Alex): the mark is identified by SHAPE (an isometric cube
// outline — recognizably a 3D CAD model), plus a `title` tooltip and
// `aria-label`. Never relies on color to be recognizable.

import { NAVY } from './theme-tokens.js';
import { OnshapeMark } from './icons.js';

// Browser link hardcodes the public Onshape host. The server uses
// ONSHAPE_BASE_URL for API calls, but the human-facing document URL is always
// the default cloud host, so it's safe to hardcode here.
const ONSHAPE_HOST = 'https://cad.onshape.com';

// onshapeDocUrl(ref): parse `did/wid/eid` → document URL, or null if malformed.
export function onshapeDocUrl(ref) {
  if (typeof ref !== 'string') return null;
  const parts = ref.trim().split('/');
  if (parts.length !== 3) return null;
  const [did, wid, eid] = parts;
  if (!did || !wid || !eid) return null;
  return `${ONSHAPE_HOST}/documents/${did}/w/${wid}/e/${eid}`;
}

// OnshapeLink({ ref }): renders the icon link, or null when ref is absent/malformed.
export function OnshapeLink({ ref }) {
  const url = onshapeDocUrl(ref);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in Onshape"
      aria-label="Open in Onshape"
      onClick={e => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: NAVY,
        lineHeight: 0,
      }}
    >
      {/* Isometric cube outline — shape carries the meaning (3D CAD model). */}
      <OnshapeMark size={14} />
    </a>
  );
}

export default OnshapeLink;
